import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Spin } from 'antd';

const ProtectedRoute = ({ children }) => {
  const { user, loading, redirectToLogin, isCheckingAuth } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  
  // Check if in dev mode - bypass authentication for testing
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

  // Effect to handle delayed redirect
  useEffect(() => {
    // Only redirect if:
    // 1. Not in dev mode
    // 2. Not loading
    // 3. Not checking auth
    // 4. No user
    // 5. Not already redirecting
    if (!isDevMode && !loading && !isCheckingAuth && !user && !shouldRedirect) {
      // Set a timer to redirect after a short delay
      const timer = setTimeout(() => {
        setShouldRedirect(true);
        redirectToLogin();
      }, 2000); // 2 second grace period for token refresh
      
      return () => clearTimeout(timer);
    }
  }, [isDevMode, loading, isCheckingAuth, user, shouldRedirect, redirectToLogin]);

  // Show loading spinner while checking authentication
  if (loading || isCheckingAuth) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: '16px', color: '#666' }}>
            Checking authentication...
          </div>
        </div>
      </div>
    );
  }

  // In dev mode, allow access without authentication for testing
  if (isDevMode) {
    console.log('🚀 DEV MODE: Bypassing authentication check - you can manually set cookies for testing');
    if (!user) {
      console.log('💡 To test authentication: Copy cookies from ebook.aristai.io and paste them here');
    }
    return children;
  }

  // If not authenticated, show loading while waiting for potential token refresh
  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: '16px', color: '#666' }}>
            {shouldRedirect ? 'Redirecting to login...' : 'Verifying authentication...'}
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated, render children
  return children;
};

export default ProtectedRoute;